import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Building2, Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { Alert } from '../../components/ui';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const portal = params.get('portal');
  const portalHome: Record<string, string> = { admin: '/admin', manager: '/manager', staff: '/staff', account: '/accounts' };
  const loginPath = (portal && portalHome[portal]) || '/admin';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const data = await api.post<{ message: string }>('/auth/reset-password', { token, password });
      setMessage(data.message || 'Password updated.');
      setTimeout(() => navigate(loginPath), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Building2 className="h-12 w-12 text-primary" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-obsidian">Reset Password</h2>
        <p className="mt-2 text-center text-sm text-taupe">Choose a new password for your account.</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {!token ? (
            <Alert>Invalid or missing reset token. Please request a new reset link.</Alert>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {message && <Alert type="success">{message}</Alert>}
              {error && <Alert>{error}</Alert>}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-charcoal">
                  New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <Lock className="h-5 w-5 text-taupe" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-charcoal">
                  Confirm New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirm"
                    name="confirm"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <Lock className="h-5 w-5 text-taupe" />
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to={loginPath} className="text-sm text-primary hover:text-primary-dark">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
